/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar } from "../components/SideBar";
import { sidebarAction } from "../reducers/slices/sidebarSlice";
import { hasPermission, isIndividual } from "../utils/utils";
import { useAppDispatch } from "../config/store";
import { PERMISSIONS } from "@advapacs/common";
import { useSelector } from "react-redux";
import { IRootState } from "../config/reducers";
import { getCriticalResultCount } from "@src/pages/criticalResult/ducks/operations";

const navTypes = {
  HEADER: "header",
  DIVIDER: "divider",
  ITEM: "item",
  GROUP: "group",
};

const SideBar = (): ReactElement => {
  const { t } = useTranslation("navbar");
  const [activeGroup, setActiveGroup] = useState("");
  const [activeItem, setActiveItem] = useState("");
  const { permissions, account } = useSelector(
    (rootState: IRootState) => rootState.loginReducer
  );
  const { countCriticalResult } = useSelector(
    (state: IRootState) => state.criticalResultReducer
  );

  const dispatch = useAppDispatch();
  const expandGroup = (id: string) => {
    setActiveGroup(id);
  };

  const selectItem = useCallback((url: string) => {
    setActiveItem(url);
  }, []);

  useEffect(() => {
    dispatch(getCriticalResultCount());

    const interval = setInterval(() => {
      dispatch(getCriticalResultCount());
    }, 90_000); // 60,000 ms = 1 minute

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = [
    {
      type: navTypes.ITEM,
      icon: "fa fa-x-ray",
      title: t("studies"),
      href: "/Studies",
      show: true,
    },
    {
      type: navTypes.ITEM,
      icon: "fa fa-circle-exclamation",
      title: t("critical_results"),
      href: "/CriticalResults",
      show: true,
      badgeCount: countCriticalResult,
    },
    {
      can: isIndividual() ? undefined : [PERMISSIONS.VIEW_DIRECTORY],
      type: navTypes.ITEM,
      icon: "fa fa-cloud",
      title: t("pacs_directory"),
      href: "/PACSDirectory",
      className: "pacs-directory",
      show:
        isIndividual() ||
        hasPermission(permissions, [PERMISSIONS.VIEW_DIRECTORY]),
    },
    {
      type: navTypes.GROUP,
      icon: "icon-cog3",
      title: t("admin"),
      href: "#",
      show: true,
      allowVisibleToOwner: true,
      can: [
        PERMISSIONS.VIEW_ROLE,
        PERMISSIONS.VIEW_USER,
        PERMISSIONS.VIEW_AUTHENTICATION,
        PERMISSIONS.VIEW_GENERAL_SETTING,
      ],
      navs: [
        {
          can: [PERMISSIONS.VIEW_ROLE],
          type: navTypes.ITEM,
          icon: "icon-users4",
          title: t("role_and_permissions"),
          href: "/Admin/RoleAndPermissions",
          show:
            !isIndividual() &&
            (hasPermission(permissions, [PERMISSIONS.VIEW_ROLE]) ||
              account.owner),
          allowVisibleToOwner: true,
        },
        {
          can: [PERMISSIONS.VIEW_USER],
          type: navTypes.ITEM,
          icon: "icon-users",
          title: t("users"),
          href: "/Users",
          show:
            !isIndividual() &&
            (hasPermission(permissions, [PERMISSIONS.VIEW_USER]) ||
              account.owner),
          allowVisibleToOwner: true,
        },
        {
          type: navTypes.ITEM,
          icon: "icon-wrench3",
          title: t("settings"),
          href: "/Admin/Settings",
          can: [
            PERMISSIONS.VIEW_AUTHENTICATION,
            PERMISSIONS.VIEW_GENERAL_SETTING,
          ],
          show: true,
        },
      ],
    },
  ];
  return (
    <>
      <Bar
        toggleSidebar={() => {
          dispatch(sidebarAction.toggle(false));
        }}
        items={items}
        expandGroup={expandGroup}
        selectItem={selectItem}
        activeGroup={activeGroup}
        activeItem={activeItem}
      />
    </>
  );
};

export default SideBar;
